import { useQuery, skipToken } from '@tanstack/react-query';
import { doorAccessLocationKey } from '../lib/queryKeys';
import { type DoorAccessLocation } from '../lib/doorAccessLocation';

export interface UseDoorAccessLocation {
  location: DoorAccessLocation | undefined;
  // A read is in flight. `isFetching` and NOT `isPending`: a skipped query stays `pending` for as
  // long as nobody has asked anything, so a card watching `isPending` would come up in a skeleton it
  // never leaves. `isFetching` needs no interpretation — a skipped read is `fetchStatus: 'idle'`.
  // The same pair `useLockCodes` already exposes for the same job.
  isFetching: boolean;
  isError: boolean;
  // The rejection itself, so the host can tell whether it has already answered it elsewhere.
  error: unknown;
  retry: () => void;
}

// The per-location read, fired ONLY on submit: never on mount and never on a keystroke. An id is
// typed digit by digit, so reading on every key would ask about `8`, `84` and `843` on the way to
// `8433` — four reads and three answers nobody asked for.
//
// The id is in the key, so submitting a second id is a second query and the first one's answer
// cannot be left sitting under the second one's number.
//
// Skipped with `skipToken` while there is no submitted id — the panel's idiom for an on-demand read
// (`useDeliveryDetail`, `useUserDevice`, `useUserDeliveries` and `useLockCodes` all use it) — and
// not `enabled: false`: the null id goes into the key as itself, with no sentinel, and the type
// narrowing comes free instead of needing a guard inside the `queryFn`.
//
// Errors stay inside (`throwOnError` left at its default), as in the count: a failed read raises the
// retry strip on this card and never takes the cards beside it down through the boundary.
export function useDoorAccessLocation(
  fetchLocation: (locationId: number) => Promise<DoorAccessLocation>,
  locationId: number | null,
): UseDoorAccessLocation {
  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey: doorAccessLocationKey(locationId),
    queryFn: locationId === null ? skipToken : () => fetchLocation(locationId),
  });

  // `refetch` is only ever called from the error strip, which exists only once a read has happened
  // — that is, when the query is NOT skipped. Over a skipped one it would do nothing.
  return { location: data, isFetching, isError, error, retry: () => void refetch() };
}
